import { useState, useEffect } from "react";
export function calculateEngagement(
  { actions, sessions, topics, sessionFrequency },
  config = {}
) {
  const normalizeStatus = (s) => (s || '').toLowerCase();
  const frequency = Number(sessionFrequency);

  if (!Array.isArray(actions) || !Array.isArray(sessions) || !Array.isArray(topics) || !frequency) {
    return {
      score: 0,
      A: 0,
      C: 0,
      T: 0,
      avgGap: 0,
      expectedGap: 0,
      level: "Unknown",
      description: "",
      topicStatuses: [],
      explanations: [],
      tips: [],
    };
  }

  const { w1 = 0.5, w2 = 0.35, w4 = 0.15 } = config;

  // A: Action Engagement
  const actionCount = actions.length;
  const completed = actions.filter((a) => normalizeStatus(a.status) === "completed").length;
  const started = actions.filter((a) => normalizeStatus(a.status) === "started").length;
  const A = actionCount ? Math.min(1, (completed + 0.5 * started) / actionCount) : 0;

  // C: Consistency
  const sortedSessions = [...sessions].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  const gaps = [];
  for (let i = 1; i < sortedSessions.length; i++) {
    const prev = new Date(sortedSessions[i - 1].timestamp);
    const curr = new Date(sortedSessions[i].timestamp);
    const deltaDays = (curr - prev) / (1000 * 60 * 60 * 24);
    if (!isNaN(deltaDays) && deltaDays >= 0) gaps.push(deltaDays);
  }
  const avgGap = gaps.length ? gaps.reduce((sum, val) => sum + val, 0) / gaps.length : 0;
  const expectedGap = 30 / frequency;
  const C = expectedGap > 0 && gaps.length > 0
    ? Math.max(0, 1 - Math.abs(avgGap - expectedGap) / expectedGap)
    : 0;

  // T: Topic Progression
  const progressedTopics = new Set();
  topics.forEach((topic) => {
    const topicIdStr = topic.id?.toString();
    const hasSession = sessions.some((s) =>
      Object.keys(s.ratings || {}).includes(topicIdStr)
    );
    const hasAction = actions.some((a) => a.topicId?.toString() === topicIdStr);
    if (hasSession && hasAction) progressedTopics.add(topicIdStr);
  });
  const T = topics.length ? Math.min(1, progressedTopics.size / topics.length) : 0;

  const topicStatuses = topics.map((topic) => {
    const topicName = topic.name?.toLowerCase();
    const topicActions = actions.filter((a) =>
      a.topicId?.toString() === topic.id?.toString() ||
      a.title?.toLowerCase().includes(topicName)
    );
    const completed = topicActions.filter((a) => normalizeStatus(a.status) === "completed").length;
    const started = topicActions.filter((a) => normalizeStatus(a.status) === "started").length;
    const open = topicActions.filter((a) => normalizeStatus(a.status) === "opened").length;
    const total = topicActions.filter((a) => normalizeStatus(a.status) !== "cancelled").length;

    let status;
    if (total === 0) status = "No Actions";
    else if (completed === total) status = "Up to Date";
    else if (started > 0 || completed > 0) status = "In Progress";
    else status = "Needs Attention";

    return {
      topicId: topic.id,
      name: topic.name,
      status,
      actions: { completed, started, open, total },
    };
  });

  const rawScore = 100 * (w1 * A + w2 * C + w4 * T);
  const score = Math.round(rawScore);

  const getEngagementLabel = (score) => {
    if (score < 20) return { level: "Disengaged", description: "Little to no involvement or commitment" };
    if (score < 40) return { level: "Minimally Engaged", description: "Irregular participation" };
    if (score < 60) return { level: "Partially Engaged", description: "Some action and progress, but not consistent" };
    if (score < 80) return { level: "Actively Engaged", description: "Consistent participation, not yet optimal flow" };
    return { level: "In Flow", description: "Fully engaged in the coaching process" };
  };

  const explanations = [];

if (A < 0.6) explanations.push("Most actions are still open.");
else if (A < 0.9) explanations.push("Some actions done, but not consistent.");
else explanations.push("You're completing actions regularly.");

if (C < 0.6) explanations.push("Sessions are too far apart.");
else if (C < 0.9) explanations.push("Session timing is a bit irregular.");
else explanations.push("Session timing looks great.");

if (T < 0.6) explanations.push("Little progress across topics.");
else if (T < 0.9) explanations.push("Some topics need more attention.");
else explanations.push("You're making solid topic progress.");

  const tips = [];
  if (A < 0.9) tips.push("Complete more actions.");
  if (C < 0.9) tips.push("Attend sessions more regularly.");
  if (T < 0.9) tips.push("Work on neglected topics."); 

  return {
    score,
    A,
    C,
    T,
    avgGap: Number(avgGap.toFixed(1)),
    expectedGap: Number(expectedGap.toFixed(1)),
    ...getEngagementLabel(score),
    topicStatuses,
    explanations,
    tips,
  };
}

export function useEngagementScore({ actions, sessions, topics, sessionFrequency }) {
  const [engagementScore, setEngagementScore] = useState(null);

  useEffect(() => {
    if (!actions || !sessions || !topics || !sessionFrequency) return;

    const result = calculateEngagement({
      actions,
      sessions,
      topics,
      sessionFrequency,
    });

    setEngagementScore(result);
  }, [actions, sessions, topics, sessionFrequency]);

  return engagementScore;
}
